import type { FC } from "react"
import { isValidElement } from "react"

import { useNavigate } from "react-router-dom"
import type { ColumnInstance, UseTableOptions } from "react-table"
import { useTable } from "react-table"
import type { FlattenSimpleInterpolation } from "styled-components"
import styled from "styled-components"
export * from "./FlexTable"

const Wrapper = styled.div<{ css?: FlattenSimpleInterpolation }>`
  ${({ css }) => css}
`
export type IdentifiedObject = { id?: string }

const nonEmpty = (value: unknown): string | undefined =>
  typeof value === `string` && value.trim() ? value.trim() : undefined

/**
 * Plain-text heading for a column, used as the `data-label` of its cells in
 * the stacked mobile layout. Admin headers are `<AdminTableHeader text="…" />`
 * or `<AdminSortHeader>…</AdminSortHeader>`, so read `text`, then a string
 * `children`; string headers are used as-is; anything else yields no label.
 */
const getColumnLabel = <MODEL extends IdentifiedObject>(
  column: ColumnInstance<MODEL>,
): string | undefined => {
  const header: unknown = column.Header
  if (isValidElement<{ text?: unknown; children?: unknown }>(header)) {
    return nonEmpty(header.props.text) ?? nonEmpty(header.props.children)
  }
  return nonEmpty(header)
}

export type TableProps<MODEL extends IdentifiedObject> =
  UseTableOptions<MODEL> & {
    name?: string
    clickable?: boolean
    className?: string
    css?: FlattenSimpleInterpolation
    onClick?: (id: string | undefined) => void
  }

export const Table = <MODEL extends IdentifiedObject>({
  data,
  columns,
  name,
  clickable,
  onClick,
  className,
  css,
}: TableProps<MODEL>): ReturnType<FC> => {
  const navigate = useNavigate()
  const isClickable = clickable ?? Boolean(onClick)
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data })

  return (
    <Wrapper className={className} css={css}>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            // eslint-disable-next-line react/jsx-key
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                // eslint-disable-next-line react/jsx-key
                <th
                  {...column.getHeaderProps({
                    style: {
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                    },
                  })}
                >
                  {column.render(`Header`)}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)

            return (
              // eslint-disable-next-line react/jsx-key
              <tr
                {...row.getRowProps()}
                className={isClickable ? `cursor-pointer` : undefined}
                onClick={() => {
                  if (onClick) {
                    return onClick(row.original.id)
                  }

                  return clickable
                    ? navigate(`/${name}/${row.original.id}`)
                    : undefined
                }}
                data-clickable={isClickable}
                data-test={`${name}-table-row`}
                data-testid={`${row.index}-table-row`}
              >
                {row.cells.map((cell) => {
                  return (
                    // eslint-disable-next-line react/jsx-key
                    <td
                      data-testid={`${cell.value}-cell`}
                      data-label={getColumnLabel(cell.column)}
                      {...cell.getCellProps()}
                    >
                      {cell.render(`Cell`)}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </Wrapper>
  )
}
